import { type Bot, MessageCtxSchema } from '@/api/schema'
import { ChatMessage } from '@/app/components/ChatMessage/ChatMessage'
import { Emoji } from '@/app/components/Emoji/Emoji'
import { Row } from '@/app/components/Flex/Flex'
import { Layout } from '@/app/components/Layout/Layout'
import { Loader } from '@/app/components/Loader/Loader'
import { Motion } from '@/app/components/Motion/Motion'
import { UI } from '@/app/components/UI/UI'
import { useBack } from '@/app/hooks/useBack'
import { useNavigate } from '@/app/hooks/useNavigate'
import CleanSvg from '@/app/icons/clean.svg?react'
import { ChatFooter } from '@/app/pages/ChatPage/ChatFooter/ChatFooter'
import { CleanPopup } from '@/app/pages/ChatPage/CleanPopup/CleanPopup'
import { useChat } from '@/app/state/chat'
import { useSession } from '@/app/state/session'
import { metrikaGoal } from '@/app/utils/metrika'
import classNames from 'classnames'
import type { FunctionalComponent } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'
import { useSearch } from 'wouter-preact'
import { useShallow } from 'zustand/shallow'
import styles from './index.module.scss'

export const ChatPage: FunctionalComponent<{
  bot: Bot
}> = props => {
  const back = useBack('~/chats')

  const bottomRef = useRef<HTMLDivElement | null>(null)
  const scrolled = useRef(false)
  const route = useNavigate()
  const search = useSearch()

  const initChat = useChat(s => s.init)
  const resetChat = useChat(s => s.reset)
  const bot = useChat(s => s.bot)
  const messageIds = useChat(useShallow(s => s.messageIds))
  const chatReady = useChat(s => s.ready)
  const messagesCount = useSession(useShallow(s => s.user?.messagesCount))
  const hasPayment = useSession(s => s.user?.hasPayment)

  const [cleanVisible, setCleanVisible] = useState(false)

  useEffect(() => {
    const params = new URLSearchParams(search)
    const q = params.get('q') ?? undefined
    const groupId = params.get('groupId') ?? undefined
    const suggestions = params.getAll('suggestion')
    const template = params.get('template') ?? undefined

    route(window.location.pathname, { replace: true })

    const ctx = MessageCtxSchema.safeParse({
      groupId,
      suggestions,
      template,
    })

    initChat(props.bot, {
      q,
      ctx: ctx.success ? ctx.data : undefined,
    })

    return () => {
      resetChat()
    }
  }, [])

  useEffect(() => {
    if (!chatReady) return

    if (messageIds.length > 0 && !scrolled.current) {
      scrolled.current = true
      bottomRef.current?.scrollIntoView({
        behavior: 'instant',
        block: 'end',
      })
    }
  }, [messageIds, chatReady])

  return (
    <>
      <Layout>
        {cleanVisible && (
          <CleanPopup
            onClose={() => {
              setCleanVisible(false)
            }}
          />
        )}

        <Layout.Content
          fullWidth
          class={styles.content}
          onBack={back}
          right={
            <>
              {hasPayment && (
                <UI.Icon class={styles.counter}>
                  <Emoji type='message' />
                  {messagesCount}
                </UI.Icon>
              )}

              <UI.Icon
                onClick={() => {
                  metrikaGoal('menu_click_clear')
                  setCleanVisible(true)
                }}
              >
                <CleanSvg strokeWidth={1.5} />
              </UI.Icon>
            </>
          }
        >
          {!chatReady
            ? (
              <Loader.Container>
                <Loader type='chat' />
              </Loader.Container>
            )
            : (
              <>
                <div class={styles.topShadow} />

                <Row
                  gap={20}
                  gapMobile={15}
                  class={styles.inner}
                >
                  <div
                    class={classNames(styles.avatar, {
                      [styles.visible]: chatReady,
                    })}
                  >
                    <Motion>
                      <UI.Avatar
                        label
                        bot={bot}
                        sizeMobile={64}
                      />
                    </Motion>
                  </div>

                  <div class={styles.messages}>
                    {messageIds.map(messageId => (
                      <ChatMessage
                        key={messageId}
                        id={messageId}
                        isLast={messageIds[messageIds.length - 1] === messageId}
                      />
                    ))}
                  </div>

                  <ChatFooter
                    bot={props.bot}
                  />
                </Row>

                <div class={styles.bottomShadow} />
              </>
            )}
        </Layout.Content>
      </Layout>

      <div ref={bottomRef} />
    </>
  )
}
